import { ScheduleSessionSurface } from "@/components/schedule/ScheduleSessionSurface";
import React, { useMemo } from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Check, Lock, Users, ChevronRight, CalendarX } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ScheduledClass } from '@/types/types';
import { Button } from '@/components/ui/button';

interface AthleteDayListProps {
  date: Date;
  classes: ScheduledClass[];
  onClassClick: (classInfo: ScheduledClass, date: Date) => void;
  isClassBooked?: (classId: string, date: Date, time: string) => boolean;
  recentlyBookedSlots?: Set<string>;
  bookingAdvanceDays?: number;
}

const getAvailableSpots = (ci: ScheduledClass): number => {
  const anyCi = ci as unknown as { spots_available?: number; booked_count?: number };
  if (typeof anyCi.spots_available === 'number') return anyCi.spots_available;
  if (typeof anyCi.booked_count === 'number') return (ci.capacity || 0) - anyCi.booked_count;
  return ci.capacity || 0;
};

const getCapacityTone = (ci: ScheduledClass): string => {
  const spots = getAvailableSpots(ci);
  if (!ci.capacity || spots <= 0) return 'schedule-capacity-full';
  const percentage = (spots / ci.capacity) * 100;
  if (percentage >= 60) return 'schedule-capacity-available';
  if (percentage >= 40) return 'schedule-capacity-limited';
  return 'schedule-capacity-nearly-full';
};

/**
 * Vue « Jour » du planning athlète : liste verticale des séances du jour,
 * avec bouton Réserver / badge Complet, conforme à la maquette VB20.
 */
export const AthleteDayList: React.FC<AthleteDayListProps> = ({
  date,
  classes,
  onClassClick,
  isClassBooked = () => false,
  recentlyBookedSlots = new Set(),
  bookingAdvanceDays = 0,
}) => {
  const { t } = useTranslation(['schedule', 'athlete', 'common']);

  const dayClasses = useMemo(() => {
    const dateISO = format(date, 'yyyy-MM-dd');
    return classes
      .filter((c) => c.class_date === dateISO)
      .sort((a, b) => (a.time || '').localeCompare(b.time || ''));
  }, [classes, date]);

  const isBeyondLimit = useMemo(() => {
    if (!bookingAdvanceDays) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const maxDate = new Date(today);
    maxDate.setDate(maxDate.getDate() + bookingAdvanceDays);
    maxDate.setHours(23, 59, 59, 999);
    return date > maxDate;
  }, [date, bookingAdvanceDays]);

  if (dayClasses.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-14 gap-2 text-center">
        <CalendarX className="h-8 w-8 text-muted-foreground" />
        <p className="text-sm font-medium text-foreground">
          {t('schedule.noClassesThisDay', { ns: 'athlete', defaultValue: 'Aucune séance ce jour' })}
        </p>
        <p className="text-xs text-muted-foreground">
          {t('schedule.noClassesThisDayHint', { ns: 'athlete', defaultValue: 'Changez de jour ou passez à la vue Semaine.' })}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2.5">
      {dayClasses.map((ci) => {
        const booked = isClassBooked(ci.id, date, ci.time);
        const spots = getAvailableSpots(ci);
        const full = spots <= 0;
        const slotKey = `${ci.id}-${ci.class_date}-${ci.time}`;
        const justBooked = recentlyBookedSlots.has(slotKey);
        const capacityTone = getCapacityTone(ci);

        return (
          <ScheduleSessionSurface key={slotKey}>
          <div
            className={cn(
              'athlete-day-slot relative flex min-h-[62px] items-center gap-3 overflow-hidden border px-3 py-2.5 transition-colors',
              booked ? 'schedule-capacity-booked' : capacityTone,
              isBeyondLimit && 'opacity-60',
              justBooked && 'ring-2 ring-green-500'
            )}
          >
            <span
              aria-hidden="true"
              className="athlete-day-slot-status absolute inset-y-0 left-0 w-2"
            />
            {/* Heure + durée */}
            <div className="flex w-14 shrink-0 flex-col pl-1">
              <span className="text-lg font-bold tabular-nums text-foreground">{ci.time}</span>
              <span className="text-xs text-muted-foreground">{ci.duration}</span>
            </div>

            {/* Nom + places */}
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-foreground">{ci.name}</p>
              <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
                {ci.category ? <span className="truncate">{ci.category}</span> : null}
                <span className="inline-flex items-center gap-0.5">
                  <Users className="h-3 w-3" />
                  {spots}
                </span>
              </p>
            </div>

            {/* Action */}
            {booked ? (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[hsl(var(--schedule-booked,var(--schedule-available))/0.15)] px-3 py-1.5 text-xs font-semibold text-[hsl(var(--schedule-booked,var(--schedule-available)))]">
                <Check className="h-3.5 w-3.5" />
                {t('booked', { ns: 'schedule', defaultValue: 'Réservé' })}
              </span>
            ) : full ? (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-destructive/15 px-3 py-1.5 text-xs font-semibold text-destructive">
                <Lock className="h-3.5 w-3.5" />
                {t('full', { ns: 'schedule', defaultValue: 'Complet' })}
              </span>
            ) : (
              <Button
                type="button"
                variant="default"
                size="sm"
                disabled={isBeyondLimit}
                onClick={() => onClassClick(ci, date)}
                 className={cn(
                   'athlete-cta inline-flex min-w-[7rem] shrink-0 items-center justify-center gap-1 rounded-md px-4 py-2 text-sm font-bold transition-transform active:scale-95',
                  isBeyondLimit && 'cursor-not-allowed opacity-50'
                )}
              >
                {t('reserve', { ns: 'schedule', defaultValue: 'Réserver' })}
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
          </ScheduleSessionSurface>
        );
      })}
    </div>
  );
};
